import React, { useState, useCallback } from 'react';
import { HStack, Text, Input, Button, useToast } from '@chakra-ui/react';

interface EditableFieldProps {
  label: string;
  value: string;
  hospitalId: string;
  fieldName: string;
  isRejected: boolean;
  onSave: (hospitalId: string, fieldName: string, value: string) => void;
}

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  hospitalId,
  fieldName,
  isRejected,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const handleSave = useCallback(async () => {
    if (!editValue.trim()) {
      toast({
        title: 'Error',
        description: 'Field cannot be empty',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      await onSave(hospitalId, fieldName, editValue.trim());
      setIsEditing(false);
      toast({
        title: 'Success',
        description: `${label} updated successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(`Failed to save ${fieldName}:`, error);
      const errorMessage = error instanceof Error ? error.message : `Failed to update ${label}`;
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }, [hospitalId, fieldName, editValue, label, onSave, toast]);

  const handleDiscard = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  return (
    <div role="group" aria-label={`Edit ${label}`}>
      <Text color="gray.500" fontSize="sm">{label}:</Text>
      {isEditing ? (
        <HStack spacing={2} mt={1}>
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            size="sm"
            flex={1}
          />
          <Button size="xs" colorScheme="blue" onClick={handleSave} isLoading={saving}>
            Save
          </Button>
          <Button size="xs" variant="outline" onClick={handleDiscard}>
            Discard
          </Button>
        </HStack>
      ) : (
        <HStack spacing={2} align="center">
          <Text fontWeight="medium" flex={1}>{value || 'N/A'}</Text>
          {isRejected && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </HStack>
      )}
    </div>
  );
};

export default EditableField;